import { useEffect, useState } from "react";
import { X } from "lucide-react";
import tagApi from "../../../../api/tagApi.js";
import { useSelector } from "react-redux";
import { useNotification } from "../../../../hooks/useNotification.jsx";

export default function AddTagModal({
  isOpen,
  setIsOpen,
  shop,
  editingTag,
  setEditingTag,
  tags,
  getTags,
}) {
  const { addNotification } = useNotification();
  const auth = useSelector((state) => state.auth);
  const [ordinalNumber, setOrdinalNumber] = useState(1);
  const [name, setName] = useState("");
  const [color, setColor] = useState("#FF0000");
  const [isActive, setIsActive] = useState(true);

  const colors = [
    "#FF0000", // Red
    "#FF6B6B", // Coral
    "#FFA07A", // Light Salmon
    "#FFA500", // Orange
    "#20B2AA", // Light Sea Green
    "#4169E1", // Royal Blue
    "#6495ED", // Cornflower Blue
    "#8A2BE2", // Blue Violet
    "#FFD700", // Gold
    "#3CB371", // Medium Sea Green
  ];

  const handleSaveClick = async () => {
    let res;
    if (!ordinalNumber || !name.trim() || !color) {
      addNotification("Vui lòng điền đầy đủ thông tin", -1);
      return;
    }
    if (ordinalNumber < 1 || ordinalNumber > tags.length + 1) {
      addNotification("STT không hợp lệ", -1);
      return;
    }
    if (editingTag) {
      res = await tagApi.updateByIds(
        tags.map((t) => {
          if (t._id === editingTag._id) {
            return {
              ...t,
              ordinalNumber,
              name,
              color,
              isActive,
            };
          } else {
            if (
              editingTag.ordinalNumber < ordinalNumber &&
              t.ordinalNumber > editingTag.ordinalNumber &&
              t.ordinalNumber <= ordinalNumber
            )
              return {
                ...t,
                ordinalNumber: t.ordinalNumber - 1,
              };
            else if (
              editingTag.ordinalNumber > ordinalNumber &&
              t.ordinalNumber < editingTag.ordinalNumber &&
              t.ordinalNumber >= ordinalNumber
            )
              return {
                ...t,
                ordinalNumber: t.ordinalNumber + 1,
              };
            return t;
          }
        })
      );
    } else {
      res = await tagApi.updateByIds(
        tags.map((t) => {
          if (t.ordinalNumber >= ordinalNumber)
            return {
              ...t,
              ordinalNumber: t.ordinalNumber + 1,
            };
          else return t;
        })
      );
      res = await tagApi.create({
        accountId: auth.user.accountId,
        shopId: shop._id,
        ordinalNumber,
        name,
        color,
        isActive,
      });
    }
    if (res.status === 1) {
      await getTags();
      addNotification("Lưu thành công", res.status);
      handleCloseClick();
    } else addNotification(res.message, res.status);
  };

  const handleCloseClick = () => {
    setIsOpen(false);
    setEditingTag(null);
    setOrdinalNumber(tags.length + 1);
    setName("");
    setColor("#FF0000");
    setIsActive(true);
  };

  useEffect(() => {
    if (editingTag) {
      setOrdinalNumber(editingTag.ordinalNumber);
      setName(editingTag.name);
      setColor(editingTag.color);
      setIsActive(editingTag.isActive);
    }
  }, [editingTag]);

  useEffect(() => {
    if (isOpen && !editingTag) {
      setOrdinalNumber(tags.length + 1);
    }
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white text-black rounded-lg w-full max-w-md p-6">
        <div className="flex justify-between items-center mb-4 pb-2 border-b">
          <h2 className="text-xl font-semibold">
            {editingTag ? "Sửa" : "Thêm"}
          </h2>
          <button
            onClick={handleCloseClick}
            className="text-gray-500 hover:text-gray-700 transition-colors duration-200"
          >
            <X size={20} />
          </button>
        </div>
        <div className="space-y-6">
          {/* STT Field */}
          <div className="flex items-center gap-4">
            <label className="w-24 text-gray-700">STT</label>
            <div className="relative">
              <input
                type="number"
                value={ordinalNumber}
                min={1}
                max={tags.length + 1}
                onChange={(e) => setOrdinalNumber(Number(e.target.value))}
                className="w-24 px-3 py-2 border rounded-md"
              />
            </div>
          </div>

          {/* Tag Name Field */}
          <div className="flex items-center gap-4">
            <label className="w-24 text-gray-700">Tên tag</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="flex-1 px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          {/* Color Picker */}
          <div className="space-y-2">
            <label className="block text-gray-700">Bộ chọn màu</label>
            <div className="flex flex-wrap gap-2">
              {colors.map((c) => (
                <button
                  key={c}
                  onClick={() => setColor(c)}
                  className={`w-8 h-8 rounded-full transition-transform ${
                    color === c
                      ? "scale-110 ring-2 ring-offset-2 ring-blue-500"
                      : ""
                  }`}
                  style={{ backgroundColor: c }}
                />
              ))}
              <div className="flex items-center gap-2">
                <input
                  type="color"
                  value={color}
                  onChange={(e) => setColor(e.target.value)}
                  className="w-8 h-8 rounded-full cursor-pointer"
                />
                <span className="text-sm text-gray-600">Tùy chọn</span>
              </div>
            </div>
          </div>

          {/* Inactive Checkbox */}
          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="inactive"
              checked={isActive}
              onChange={(e) => setIsActive(e.target.checked)}
              className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
            />
            <label htmlFor="inactive" className="text-gray-700">
              Hoạt động
            </label>
          </div>

          {/* Save Button */}
          <div className="flex justify-end pt-4">
            <button
              onClick={handleSaveClick}
              className="px-6 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors"
            >
              Lưu
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
